<template>
  <div class="portafolio-busqueda">
    <!-- Hero Section -->
    <v-parallax
      src="https://images.unsplash.com/photo-1460925895917-afdab827c52f"
      height="300"
    >
      <v-container fill-height>
        <v-row align="center" justify="center" class="text-center">
          <v-col cols="12">
            <h1 class="display-2 font-weight-bold white--text mb-4">
              Nuestro Portafolio
            </h1>
            <h2 class="headline white--text font-weight-light">
              Descubre nuestros trabajos y proyectos
            </h2>
          </v-col>
        </v-row>
      </v-container>
    </v-parallax>

    <!-- Buscador y Filtros -->
    <v-container class="mt-6">
      <v-card elevation="5" class="pa-4">
        <v-row align="center">
          <!-- Buscador -->
          <v-col cols="12" md="5">
            <v-text-field
              v-model="searchTerm"
              label="Buscar proyecto..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details
              @keyup.enter="buscarProyectos"
            ></v-text-field>
          </v-col>

          <!-- Filtro por categoría -->
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedCategory"
              :items="categories"
              item-text="name"
              item-value="id"
              label="Categoría"
              outlined
              dense
              clearable
              hide-details
            ></v-select>
          </v-col>

          <!-- Ordenar -->
          <v-col cols="12" md="2">
            <v-select
              v-model="sortBy"
              :items="sortOptions"
              label="Ordenar por"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>

          <!-- Botón reset -->
          <v-col cols="12" md="2" class="text-right">
            <v-btn color="#d7641a" outlined @click="resetFilters">
              <v-icon left>mdi-refresh</v-icon>
              Limpiar
            </v-btn>
          </v-col>
        </v-row>

        <!-- Tags populares -->
        <v-row class="mt-4">
          <v-col cols="12">
            <div class="d-flex align-center flex-wrap">
              <span class="caption grey--text mr-3">Tags populares:</span>
              <v-chip
                v-for="tag in popularTags"
                :key="tag"
                small
                outlined
                class="mr-2 mb-1"
                :color="selectedTags.includes(tag) ? '#d7641a' : 'grey'"
                @click="toggleTag(tag)"
              >
                #{{ tag }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </v-container>

    <!-- Resultados -->
    <v-container class="mt-6">
      <!-- Contador de resultados y vista -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="d-flex justify-space-between align-center flex-wrap">
            <span class="subtitle-1 grey--text">
              Mostrando {{ paginatedProjects.length }} de
              {{ filteredProjects.length }} proyectos
            </span>
            <div>
              <v-btn-toggle v-model="viewMode" mandatory dense class="elevation-0">
                <v-btn value="grid">
                  <v-icon>mdi-view-grid</v-icon>
                </v-btn>
                <v-btn value="list">
                  <v-icon>mdi-view-list</v-icon>
                </v-btn>
              </v-btn-toggle>
            </div>
          </div>
          <v-divider class="mt-2"></v-divider>
        </v-col>
      </v-row>

      <!-- Vista Grid (Tarjetas) -->
      <v-row v-if="viewMode === 'grid'">
        <v-col
          v-for="proyecto in paginatedProjects"
          :key="proyecto.id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-hover v-slot="{ hover }">
            <v-card
              :elevation="hover ? 12 : 4"
              class="project-card"
              @click="irAlProyecto(proyecto)"
            >
              <!-- Miniatura del sitio -->
              <div class="thumbnail-container">
                <v-img
                  :src="proyecto.thumbnail"
                  height="200"
                  class="project-thumbnail"
                  :class="{ 'thumbnail-hover': hover }"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular
                        indeterminate
                        color="#d7641a"
                      ></v-progress-circular>
                    </v-row>
                  </template>

                  <!-- Overlay en hover -->
                  <div class="thumbnail-overlay" v-if="hover">
                    <v-btn
                      color="#d7641a"
                      dark
                      rounded
                      small
                      @click.stop="irAlProyecto(proyecto)"
                    >
                      <v-icon left small>mdi-open-in-new</v-icon>
                      Ver Proyecto
                    </v-btn>
                  </div>
                </v-img>
              </div>

              <v-card-title class="pb-2 text-subtitle-1 font-weight-bold">
                {{ proyecto.title }}
                <v-chip v-if="proyecto.isNew" small color="#4CAF50" dark class="ml-2">
                  Nuevo
                </v-chip>
              </v-card-title>

              <v-card-text class="pt-0">
                <div class="d-flex align-center mb-2">
                  <v-icon small class="mr-1" color="#d7641a">mdi-tag</v-icon>
                  <span class="caption">{{ proyecto.categoryName }}</span>
                </div>

                <p class="body-2 grey--text text--darken-2 mb-3">
                  {{ proyecto.shortDescription }}
                </p>

                <!-- Tags -->
                <div class="d-flex flex-wrap">
                  <v-chip
                    v-for="(tag, idx) in proyecto.tags.slice(0, 3)"
                    :key="idx"
                    x-small
                    outlined
                    class="mr-1 mb-1"
                  >
                    #{{ tag }}
                  </v-chip>
                  <span v-if="proyecto.tags.length > 3" class="caption grey--text">
                    +{{ proyecto.tags.length - 3 }}
                  </span>
                </div>
              </v-card-text>

              <v-card-actions>
                <v-btn text color="#d7641a" block @click.stop="irAlProyecto(proyecto)">
                  Ver Proyecto
                  <v-icon right small>mdi-arrow-right</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-hover>
        </v-col>
      </v-row>

      <!-- Vista Lista -->
      <v-row v-else>
        <v-col cols="12">
          <v-list two-line class="elevation-1">
            <v-list-item
              v-for="proyecto in paginatedProjects"
              :key="proyecto.id"
              @click="irAlProyecto(proyecto)"
              class="project-list-item"
            >
              <v-list-item-avatar tile size="80" class="mr-4">
                <v-img :src="proyecto.thumbnail" contain></v-img>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="title">
                  {{ proyecto.title }}
                  <v-chip v-if="proyecto.isNew" small color="#4CAF50" dark class="ml-2">
                    Nuevo
                  </v-chip>
                </v-list-item-title>

                <v-list-item-subtitle class="mt-1">
                  <v-icon small class="mr-1" color="#d7641a">mdi-tag</v-icon>
                  {{ proyecto.categoryName }}
                  <span class="mx-2">•</span>
                  <v-icon small class="mr-1">mdi-calendar</v-icon>
                  {{ proyecto.date }}
                </v-list-item-subtitle>

                <v-list-item-subtitle class="mt-2">
                  {{ proyecto.shortDescription }}
                </v-list-item-subtitle>

                <div class="d-flex flex-wrap mt-2">
                  <v-chip
                    v-for="(tag, idx) in proyecto.tags.slice(0, 5)"
                    :key="idx"
                    x-small
                    outlined
                    class="mr-1 mb-1"
                  >
                    #{{ tag }}
                  </v-chip>
                </div>
              </v-list-item-content>

              <v-list-item-action>
                <v-btn icon color="#d7641a">
                  <v-icon>mdi-open-in-new</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>

      <!-- No resultados -->
      <v-row v-if="filteredProjects.length === 0" justify="center" class="my-12">
        <v-col cols="12" text-center>
          <v-icon size="80" color="grey lighten-2">mdi-folder-search</v-icon>
          <h3 class="mt-4 grey--text">No se encontraron proyectos</h3>
          <p class="grey--text">Intenta con otros términos de búsqueda</p>
          <v-btn color="#d7641a" outlined @click="resetFilters"> Limpiar filtros </v-btn>
        </v-col>
      </v-row>

      <!-- Paginación -->
      <v-row v-if="totalPages > 1" class="mt-6">
        <v-col cols="12">
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            color="#d7641a"
            @input="cambiarPagina"
          ></v-pagination>
        </v-col>
      </v-row>
    </v-container>

    <!-- Footer -->
  </div>
</template>

<script>
export default {
  name: "PortafolioBusqueda",
  data() {
    return {
      searchTerm: "",
      selectedCategory: null,
      selectedTags: [],
      sortBy: "recientes",
      viewMode: "grid",
      currentPage: 1,
      itemsPerPage: 12,
      sortOptions: [
        { text: "Más recientes", value: "recientes" },
        { text: "Más antiguos", value: "antiguos" },
        { text: "A - Z", value: "az" },
        { text: "Z - A", value: "za" },
      ],
      categories: [
        { id: "beauty", name: "Belleza" },
        { id: "mechanics", name: "Mecánica" },
        { id: "school", name: "Educación" },
        { id: "restaurant", name: "Restaurantes" },
        { id: "health", name: "Salud" },
        { id: "ecommerce", name: "E-commerce" },
        { id: "corporate", name: "Corporativo" },
      ],
      projects: [
        // Belleza
        {
          id: 1,
          title: "Salón Bella Vida",
          categoryId: "beauty",
          categoryName: "Belleza",
          thumbnail: "https://images.unsplash.com/photo-1595476108010-b4d1f102b1b1?w=400",
          url: "/portafolio/beauty-salon",
          shortDescription:
            "Landing page moderna para salón de belleza con sistema de reservas",
          fullDescription:
            "Sitio web completo con galería de trabajos, sistema de citas online, catálogo de servicios y blog de tendencias.",
          tags: ["landing-page", "reservas", "galería", "moderno"],
          date: "2024",
          isNew: true,
          technologies: ["Vue.js", "Vuetify", "Node.js", "MongoDB"],
        },
        {
          id: 2,
          title: "Spa Zen",
          categoryId: "beauty",
          categoryName: "Belleza",
          thumbnail: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400",
          url: "/portafolio/spa-zen",
          shortDescription: "Sitio web relajante para spa con e-commerce de productos",
          fullDescription:
            "Diseño minimalista y relajante con tienda online de productos de spa, sistema de membresías y reservas.",
          tags: ["spa", "e-commerce", "bienestar", "minimalista"],
          date: "2023",
          isNew: false,
          technologies: ["React", "Stripe", "WordPress"],
        },
        // Mecánica
        {
          id: 3,
          title: "Taller Express",
          categoryId: "mechanics",
          categoryName: "Mecánica",
          thumbnail: "https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=400",
          url: "/portafolio/taller-express",
          shortDescription: "Sistema de gestión para taller mecánico",
          fullDescription:
            "Plataforma completa con gestión de órdenes de trabajo, inventario de repuestos y facturación electrónica.",
          tags: ["gestión", "taller", "inventario", "facturación"],
          date: "2024",
          isNew: true,
          technologies: ["Angular", "Spring Boot", "MySQL"],
        },
        {
          id: 4,
          title: "Autoparts Store",
          categoryId: "mechanics",
          categoryName: "Mecánica",
          thumbnail: "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400",
          url: "/portafolio/autoparts-store",
          shortDescription: "E-commerce de repuestos automotrices",
          fullDescription:
            "Tienda online con catálogo de repuestos, filtros avanzados y cotizador automático.",
          tags: ["e-commerce", "repuestos", "cotizador"],
          date: "2023",
          isNew: false,
          technologies: ["Vue.js", "Laravel", "MySQL"],
        },
        // Educación
        {
          id: 5,
          title: "Colegio Republica del Ecuador",
          categoryId: "school",
          categoryName: "Educación",
          thumbnail: "data:image/jpeg;base64,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",
          url: "/portafolio/colegio-san-juan",
          shortDescription: "Plataforma educativa con gestión académica",
          fullDescription:
            "Sistema completo para colegio con notas, asistencia, comunicación padres-docentes y biblioteca virtual.",
          tags: ["educación", "LMS", "colegio", "académico"],
          date: "2024",
          isNew: true,
          technologies: ["Angular", "Spring Boot", "PostgreSQL"],
        },
        {
          id: 6,
          title: "Academia Online",
          categoryId: "school",
          categoryName: "Educación",
          thumbnail: "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=400",
          url: "/portafolio/academia-online",
          shortDescription: "Plataforma de cursos online",
          fullDescription:
            "LMS con videos, certificaciones, foros y seguimiento de progreso para estudiantes.",
          tags: ["cursos", "online", "certificación", "LMS"],
          date: "2024",
          isNew: false,
          technologies: ["React", "Node.js", "MongoDB"],
        },
        // Restaurantes
        {
          id: 7,
          title: "Restaurante Sabores",
          categoryId: "restaurant",
          categoryName: "Restaurantes",
          thumbnail: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400",
          url: "/portafolio/restaurante-sabores",
          shortDescription: "Menú digital con pedidos QR",
          fullDescription:
            "Sistema de pedidos mediante código QR, menú interactivo y gestión de reservas.",
          tags: ["restaurante", "menú-digital", "QR", "pedidos"],
          date: "2024",
          isNew: true,
          technologies: ["Vue.js", "Laravel", "MySQL"],
        },
        {
          id: 8,
          title: "Pizzería Italia",
          categoryId: "restaurant",
          categoryName: "Restaurantes",
          thumbnail: "https://images.unsplash.com/photo-1552566626-52f8b828add9?w=400",
          url: "/portafolio/pizzeria-italia",
          shortDescription: "Landing page con sistema de delivery",
          fullDescription:
            "Sitio web con menú online, sistema de delivery y loyalty program.",
          tags: ["delivery", "pizza", "lealtad"],
          date: "2023",
          isNew: false,
          technologies: ["WordPress", "WooCommerce", "PHP"],
        },
        // Salud
        {
          id: 9,
          title: "Clínica Salud",
          categoryId: "health",
          categoryName: "Salud",
          thumbnail: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400",
          url: "/portafolio/clinica-salud",
          shortDescription: "Sistema de gestión médica",
          fullDescription:
            "Historia clínica electrónica, gestión de citas, recetas digitales y telemedicina.",
          tags: ["clínica", "historia-clínica", "telemedicina"],
          date: "2024",
          isNew: true,
          technologies: ["Angular", "Spring Boot", "PostgreSQL", "WebRTC"],
        },
        {
          id: 10,
          title: "Farmacia Plus",
          categoryId: "health",
          categoryName: "Salud",
          thumbnail: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400",
          url: "/portafolio/farmacia-plus",
          shortDescription: "E-commerce de productos farmacéuticos",
          fullDescription:
            "Tienda online con sistema de recetas médicas y delivery a domicilio.",
          tags: ["farmacia", "e-commerce", "recetas"],
          date: "2024",
          isNew: false,
          technologies: ["Vue.js", "Django", "PostgreSQL"],
        },
        // E-commerce
        {
          id: 11,
          title: "Fashion Store",
          categoryId: "ecommerce",
          categoryName: "E-commerce",
          thumbnail: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400",
          url: "/portafolio/fashion-store",
          shortDescription: "Tienda de ropa online",
          fullDescription:
            "E-commerce completo con carrito, pasarela de pagos, gestión de inventario y envíos.",
          tags: ["moda", "e-commerce", "carrito", "pagos"],
          date: "2024",
          isNew: true,
          technologies: ["React", "Node.js", "MongoDB", "Stripe"],
        },
        // Corporativo
        {
          id: 12,
          title: "Tech Solutions",
          categoryId: "corporate",
          categoryName: "Corporativo",
          thumbnail: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400",
          url: "/portafolio/tech-solutions",
          shortDescription: "Sitio web corporativo",
          fullDescription:
            "Landing page moderna para empresa de tecnología con portfolio y blog.",
          tags: ["corporativo", "landing-page", "tecnología"],
          date: "2024",
          isNew: false,
          technologies: ["Vue.js", "Vuetify", "Firebase"],
        },
      ],
    };
  },
  computed: {
    filteredProjects() {
      let filtered = [...this.projects];

      // Filtrar por búsqueda
      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        filtered = filtered.filter(
          (p) =>
            p.title.toLowerCase().includes(term) ||
            p.shortDescription.toLowerCase().includes(term) ||
            p.fullDescription.toLowerCase().includes(term) ||
            p.tags.some((tag) => tag.toLowerCase().includes(term))
        );
      }

      // Filtrar por categoría
      if (this.selectedCategory) {
        filtered = filtered.filter((p) => p.categoryId === this.selectedCategory);
      }

      // Filtrar por tags
      if (this.selectedTags.length > 0) {
        filtered = filtered.filter((p) =>
          this.selectedTags.some((tag) => p.tags.includes(tag))
        );
      }

      return filtered;
    },
    sortedProjects() {
      let sorted = [...this.filteredProjects];

      switch (this.sortBy) {
        case "recientes":
          sorted.sort((a, b) => {
            if (a.isNew && !b.isNew) return -1;
            if (!a.isNew && b.isNew) return 1;
            return new Date(b.date) - new Date(a.date);
          });
          break;
        case "antiguos":
          sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
          break;
        case "az":
          sorted.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case "za":
          sorted.sort((a, b) => b.title.localeCompare(a.title));
          break;
      }

      return sorted;
    },
    popularTags() {
      const tagsCount = {};
      this.projects.forEach((project) => {
        project.tags.forEach((tag) => {
          tagsCount[tag] = (tagsCount[tag] || 0) + 1;
        });
      });
      return Object.keys(tagsCount)
        .sort((a, b) => tagsCount[b] - tagsCount[a])
        .slice(0, 10);
    },
    totalPages() {
      return Math.ceil(this.sortedProjects.length / this.itemsPerPage);
    },
    paginatedProjects() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.sortedProjects.slice(start, end);
    },
  },
  watch: {
    searchTerm() {
      this.currentPage = 1;
    },
    selectedCategory() {
      this.currentPage = 1;
    },
    selectedTags: {
      handler() {
        this.currentPage = 1;
      },
      deep: true,
    },
    sortBy() {
      this.currentPage = 1;
    },
  },
  methods: {
    buscarProyectos() {
      this.currentPage = 1;
    },
    resetFilters() {
      this.searchTerm = "";
      this.selectedCategory = null;
      this.selectedTags = [];
      this.sortBy = "recientes";
      this.currentPage = 1;
    },
    toggleTag(tag) {
      const index = this.selectedTags.indexOf(tag);
      if (index > -1) {
        this.selectedTags.splice(index, 1);
      } else {
        this.selectedTags.push(tag);
      }
      this.currentPage = 1;
    },
    cambiarPagina(page) {
      this.currentPage = page;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    irAlProyecto(proyecto) {
      window.location.href = proyecto.url;
    },
  },
};
</script>

<style scoped>
.portafolio-busqueda {
  background: #f5f5f5;
  min-height: 100vh;
}

.project-card {
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.project-card:hover {
  transform: translateY(-8px);
}

.thumbnail-container {
  position: relative;
  overflow: hidden;
  border-radius: 4px 4px 0 0;
}

.project-thumbnail {
  transition: transform 0.3s ease;
}

.thumbnail-hover {
  transform: scale(1.05);
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.project-list-item {
  cursor: pointer;
  transition: background 0.3s ease;
}

.project-list-item:hover {
  background: #f5f5f5;
}

/* Animaciones */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.v-col {
  animation: fadeIn 0.5s ease;
}

/* Responsive */
@media (max-width: 600px) {
  .display-2 {
    font-size: 1.8rem !important;
  }

  .headline {
    font-size: 1.1rem !important;
  }

  .project-card {
    margin-bottom: 16px;
  }

  .v-list-item__avatar {
    margin-right: 0 !important;
  }
}

@media (max-width: 960px) {
  .project-list-item {
    flex-wrap: wrap;
  }

  .v-list-item__action {
    margin-top: 16px;
  }
}
</style>
